'use es6';

import { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import emptyFunction from 'react-utils/emptyFunction';
import EPBreakpointControls from 'editor-components/header/EPBreakpointControls';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { selectBreakpoint } from 'ContentEditorUI/redux/actions/styleBreakpointsActions';
import { getActiveBreakpointPreviewWidth, getIsInDefaultBreakpointMode, getResponsiveBreakpoints } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import { getActiveSidebarPanel } from 'ContentEditorUI/redux/selectors/sidebarSelectors';
import { SIDEBAR_PANELS } from 'ContentEditorUI/lib/sidebar/constants';
import { getPreviewIframeNeedsRefresh } from 'ContentEditorUI/redux/selectors/inpageSelectors';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { getIsUngatedForCustomBreakpoints } from 'ContentEditorUI/redux/selectors/authSelectors';
import { DesktopIcon } from 'foundations-assets/icons/components/DesktopIcon';
import { MobileIcon } from 'foundations-assets/icons/components/MobileIcon';
import { TabletIcon } from 'foundations-assets/icons/components/TabletIcon';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT = 'default';
const DESKTOP = 'desktop';
const MOBILE = 'mobile';
const TABLET = 'tablet';
const BUILTIN_BREAKPOINTS = [DEFAULT, DESKTOP, MOBILE, TABLET];
const BUILTIN_BREAKPOINT_LABELS = {
  default: 'components.header.deviceStylingControls.desktop',
  mobile: 'components.header.deviceStylingControls.mobile',
  tablet: 'components.header.deviceStylingControls.tablet'
};
function getLabelForBreakpoint(breakpointName, label) {
  if (BUILTIN_BREAKPOINT_LABELS[breakpointName]) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: BUILTIN_BREAKPOINT_LABELS[breakpointName]
    });
  } else {
    // custom breakpoint, no translated label for this
    // TODO: Figure out how we want to provide translated labels, was punted on for POC
    return /*#__PURE__*/_jsx(Fragment, {
      children: label || breakpointName
    });
  }
}
function getIconForBreakpoint(breakpointName) {
  // Temporary icon logic, this will probably get reimagined before we ship
  // breakpoint styles. This is mostly to stop warnings in console about
  // not using valid icon names.
  switch (breakpointName) {
    case DEFAULT:
    case DESKTOP:
      return DesktopIcon;
    case MOBILE:
      return MobileIcon;
    case TABLET:
      return TabletIcon;
    default:
      return TabletIcon;
  }
}
const StyleBreakPointsControlsContainer = ({
  defaultDevice,
  hidePreviewWidth = false,
  onChange = emptyFunction,
  toggleModuleEditSidebarCallback
}) => {
  const dispatch = useDispatch();
  const activeBreakpointPreviewWidth = useSelector(getActiveBreakpointPreviewWidth);
  const activeSidebarPanel = useSelector(getActiveSidebarPanel);
  const breakpoints = useSelector(getResponsiveBreakpoints);
  const isInDefaultBreakpointMode = useSelector(getIsInDefaultBreakpointMode);
  const previewIframeNeedsRefresh = useSelector(getPreviewIframeNeedsRefresh);
  const isUngatedForCustomBreakpoints = useSelector(getIsUngatedForCustomBreakpoints);
  const {
    builtinBreakpoints,
    customBreakpoints
  } = useMemo(() => {
    return breakpoints.reduce((lists, breakpoint) => {
      const {
        name,
        label,
        previewWidth
      } = breakpoint;
      if (isUngatedForCustomBreakpoints) {
        if (BUILTIN_BREAKPOINTS.includes(name)) {
          lists.builtinBreakpoints.push({
            icon: getIconForBreakpoint(name),
            label,
            name,
            previewWidth,
            tooltip: getLabelForBreakpoint(name, label)
          });
        } else {
          lists.customBreakpoints.push({
            label,
            name,
            previewWidth
          });
        }
      } else {
        if (name === MOBILE || name === DEFAULT) {
          lists.builtinBreakpoints.push({
            icon: getIconForBreakpoint(name),
            label,
            name,
            previewWidth,
            tooltip: getLabelForBreakpoint(name, label)
          });
        }
      }
      return lists;
    }, {
      builtinBreakpoints: [],
      customBreakpoints: []
    });
  }, [breakpoints, isUngatedForCustomBreakpoints]);
  const handleBreakpointSelect = e => {
    if (previewIframeNeedsRefresh && isInDefaultBreakpointMode && activeSidebarPanel === SIDEBAR_PANELS.edit && toggleModuleEditSidebarCallback) {
      // This is currently used to call apply on ModuleEditCMv2, so that content changes are
      // picked up on the page before switching to mobile editing mode
      toggleModuleEditSidebarCallback();
    }
    const selectedBreakpointName = e.target.value === 'desktop' ? 'default' : e.target.value;
    const selectedBreakpoint = breakpoints.find(breakpoint => breakpoint.name === selectedBreakpointName);
    dispatch(selectBreakpoint(selectedBreakpoint));
    onChange(e);
    UsageTracker.trackEditorInteraction({
      action: 'Switch breakpoints',
      event: selectedBreakpointName
    });
  };
  return /*#__PURE__*/_jsx(EPBreakpointControls, {
    breakpoints: builtinBreakpoints,
    buttonSize: "medium",
    defaultDevice: defaultDevice,
    extraBreakpoints: customBreakpoints,
    onBreakpointSelect: handleBreakpointSelect,
    previewWidth: !hidePreviewWidth ? activeBreakpointPreviewWidth : null
  });
};
StyleBreakPointsControlsContainer.propTypes = {
  defaultDevice: PropTypes.string,
  hidePreviewWidth: PropTypes.bool,
  onChange: PropTypes.func,
  toggleModuleEditSidebarCallback: PropTypes.func
};
export default StyleBreakPointsControlsContainer;